/**
 * Adressen: intern strukturiert (Strasse und Hausnummer GETRENNT), das Original
 * aus dem Kanal bleibt daneben erhalten (`roh_adresse1/2`). Woo liefert die
 * Hausnummer im Strassenfeld (100 % der Bestellungen, Recherche 03 §5), DHL
 * verlangt sie getrennt (Recherche 02 §3).
 */
import { z } from 'zod';

export const dropPointSchema = z.discriminatedUnion('typ', [
  z.object({ typ: z.literal('packstation'), lockerId: z.number().int().min(100).max(999), postNumber: z.string().regex(/^\d{6,10}$/) }),
  z.object({ typ: z.literal('postfiliale'), retailId: z.number().int().min(401).max(999), postNumber: z.string().regex(/^\d{6,10}$/).nullable() }),
]);
export type DropPoint = z.infer<typeof dropPointSchema>;

export const PARSE_QUALITAET = ['ok', 'unsicher', 'packstation', 'fehlt_postnummer', 'manuell'] as const;
export type ParseQualitaet = (typeof PARSE_QUALITAET)[number];

/** Die strukturierte Adresse, wie sie Carrier und Belege brauchen. */
export const adresseSchema = z.object({
  vorname: z.string().default(''),
  nachname: z.string().min(1),
  firma: z.string().nullable().default(null),
  strasse: z.string().min(1),
  hausnummer: z.string().default(''),
  zusatz: z.string().nullable().default(null),
  plz: z.string().min(1),
  ort: z.string().min(1),
  bundesland: z.string().nullable().default(null),
  land: z.string().regex(/^[A-Z]{2}$/),
  email: z.string().nullable().default(null),
  telefon: z.string().nullable().default(null),
  drop_point: dropPointSchema.nullable().default(null),
});
export type Adresse = z.infer<typeof adresseSchema>;

export interface ParseErgebnis {
  strasse: string;
  hausnummer: string;
  zusatz: string | null;
  drop_point: DropPoint | null;
  qualitaet: ParseQualitaet;
}

const HAUSNUMMER = /^(.*?[^\d\s])\s+(\d{1,5}\s?[a-zA-Z]?(?:\s?[-/]\s?\d{1,5}\s?[a-zA-Z]?)?)\s*$/u;
const HAUSNUMMER_VORN = /^(\d{1,5}\s?[a-zA-Z]?)[,\s]+(.+)$/u;
const POSTNUMMER = /\b(\d{6,10})\b/;

/**
 * Zerlegt Woo `address_1`/`address_2` in Strasse, Hausnummer, Zusatz.
 * Erkennt Packstation und Postfiliale (Text in address_1/2 oder Firma).
 * Niemals werfen: unsichere Faelle kommen als `unsicher` zurueck und landen
 * im Klaerfall `adresse`, nicht in einer Exception.
 */
export function parseStrasse(adresse1: string, adresse2 = '', firma = ''): ParseErgebnis {
  const a1 = adresse1.replace(/\s+/g, ' ').trim();
  const a2 = adresse2.replace(/\s+/g, ' ').trim();
  const alles = `${a1} ${a2} ${firma}`;

  const pack = /packstation\s*(\d{3})/i.exec(alles);
  if (pack || /packstation/i.test(alles)) {
    const post = POSTNUMMER.exec(alles.replace(pack?.[0] ?? '', ''));
    if (pack && post) {
      return { strasse: 'Packstation', hausnummer: pack[1], zusatz: null, qualitaet: 'packstation',
        drop_point: { typ: 'packstation', lockerId: Number(pack[1]), postNumber: post[1] } };
    }
    return { strasse: a1 || 'Packstation', hausnummer: pack?.[1] ?? '', zusatz: a2 || null, drop_point: null, qualitaet: 'fehlt_postnummer' };
  }
  const fil = /postfiliale\s*(\d{3})/i.exec(alles);
  if (fil) {
    const post = POSTNUMMER.exec(alles.replace(fil[0], ''));
    return { strasse: 'Postfiliale', hausnummer: fil[1], zusatz: null, qualitaet: 'packstation',
      drop_point: { typ: 'postfiliale', retailId: Number(fil[1]), postNumber: post?.[1] ?? null } };
  }

  const m = HAUSNUMMER.exec(a1);
  if (m) return { strasse: m[1].trim(), hausnummer: m[2].replace(/\s+/g, ''), zusatz: a2 || null, drop_point: null, qualitaet: 'ok' };
  const v = HAUSNUMMER_VORN.exec(a1);
  if (v) return { strasse: v[2].trim(), hausnummer: v[1].replace(/\s+/g, ''), zusatz: a2 || null, drop_point: null, qualitaet: 'ok' };
  // Keine Ziffer in address_1: vielleicht steht die Nummer in address_2 (z. B. "12a").
  if (a2 && /^\d{1,5}\s?[a-zA-Z]?$/.test(a2)) return { strasse: a1, hausnummer: a2.replace(/\s+/g, ''), zusatz: null, drop_point: null, qualitaet: 'ok' };
  return { strasse: a1, hausnummer: '', zusatz: a2 || null, drop_point: null, qualitaet: 'unsicher' };
}

/**
 * Feldgrenzen fuer das Label (W10/R58). Vorgabe Inhaber: hoechstens 35 Zeichen je Adressfeld. Das ist
 * strenger als DHL Parcel DE v2 (Name/Strasse 50, Ort 40) und deckt den Fallback Shipcloud ab, dessen
 * Carrier-Felder bei 35 Zeichen enden. Liefert Verstoesse als lesbare Texte mit Feldname; leer = ok.
 */
export const ADRESS_FELD_MAX = 35;

export function pruefeCarrierGrenzen(a: Adresse): string[] {
  const fehler: string[] = [];
  const zuLang = (feld: string, wert: string | null | undefined, max = ADRESS_FELD_MAX) => {
    const n = (wert ?? '').trim().length;
    if (n > max) fehler.push(`${feld} hat ${n} Zeichen (höchstens ${max})`);
  };
  zuLang('Name', `${a.vorname} ${a.nachname}`);
  zuLang('Firma', a.firma);
  zuLang('Straße', a.strasse);
  zuLang('Hausnummer', a.hausnummer, 10);
  zuLang('Adresszusatz', a.zusatz);
  zuLang('Ort', a.ort);
  if (a.plz.length < 3 || a.plz.length > 10) fehler.push('PLZ ausserhalb 3 bis 10 Zeichen');
  if (!a.drop_point && !a.hausnummer) fehler.push('Hausnummer fehlt');
  return fehler;
}

/** Vergleich zweier Adressen fuer „Lieferadresse weicht von Rechnungsadresse ab" (Geschenk-Lieferschein). */
export function adressenGleich(a: Adresse, b: Adresse): boolean {
  const norm = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
  return ['vorname', 'nachname', 'firma', 'strasse', 'hausnummer', 'plz', 'ort', 'land'].every(
    (k) => norm((a as Record<string, unknown>)[k] as string) === norm((b as Record<string, unknown>)[k] as string),
  );
}
